import React, { useState, useEffect, useCallback } from 'react'
import theme from '../../config/theme'
import Layout from '../../layout/layout'
import Input from '../../common/input'
import Table from '../../common/table'
import Pagination from '../../common/pagination'
import DatePickerPeriod, { DatePickerPeriodProps } from '../../common/datepickerPeriod'
import SnackBar from '../../common/snackBar'
import { IoDownloadOutline, IoCalendarOutline, IoClose } from 'react-icons/io5'
import { TrackingLogDto } from '../../dtos/trackingLog.dto'
import { trackingLogApi } from '../../utils/apiClient'
import { formatDate, formatDateForTag } from '../../utils/format'
import { getErrorMessage } from '../../utils/errorMessages'

const TrackingLogPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(20) // Match API default size
  const [datePicker, setDatePicker] = useState<DatePickerPeriodProps | null>(null)
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const dateButtonRef = React.useRef<HTMLButtonElement>(null)

  // API data states
  const [trackingLogs, setTrackingLogs] = useState<TrackingLogDto[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [totalItems, setTotalItems] = useState(0)
  const [totalPages, setTotalPages] = useState(0)

  // Snackbar states
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  // Convert Date to YYYY-MM-DD format for API
  const formatDateForApi = (date: Date | null): string | undefined => {
    if (!date) return undefined
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  // Fetch tracking logs from API
  const fetchTrackingLogs = useCallback(async (
    employeeName?: string,
    startDateStr?: string,
    endDateStr?: string,
    page: number = 0
  ) => {
    setIsLoading(true)
    setError(null)

    try {
      // Convert UI page (1-based) to API page (0-based)
      const apiPage = page - 1

      const response = await trackingLogApi.searchTrackingLogs(
        undefined, // employeeId - not used for now
        employeeName?.trim() || undefined,
        startDateStr,
        endDateStr,
        apiPage,
        itemsPerPage
      )

      setTrackingLogs(response.data || [])
      setTotalItems(response.meta?.totalItems || 0)
      setTotalPages(response.meta?.totalPages || 0)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch tracking logs'
      setError(errorMessage)
      setTrackingLogs([])
      setTotalItems(0)
      setTotalPages(0)
    } finally {
      setIsLoading(false)
    }
  }, [itemsPerPage])

  // Search query state for debounced fetching
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')
  const [debouncedStartDate, setDebouncedStartDate] = useState<Date | null>(null)
  const [debouncedEndDate, setDebouncedEndDate] = useState<Date | null>(null)

  // Debounced search - update debouncedSearchQuery after user stops typing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery)
      setDebouncedStartDate(startDate)
      setDebouncedEndDate(endDate)
      // Reset to page 1 when search/filter changes
      setCurrentPage(1)
    }, 500) // 500ms debounce

    return () => clearTimeout(timeoutId)
  }, [searchQuery, startDate, endDate])

  // Fetch tracking logs when page or debounced search/filters change
  useEffect(() => {
    fetchTrackingLogs(
      debouncedSearchQuery,
      formatDateForApi(debouncedStartDate),
      formatDateForApi(debouncedEndDate),
      currentPage
    )
  }, [currentPage, debouncedSearchQuery, debouncedStartDate, debouncedEndDate, fetchTrackingLogs])

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  // Handle export
  const handleExport = async () => {
    try {
      // Get current filter values
      const employeeName = debouncedSearchQuery || undefined
      const startDateStr = formatDateForApi(debouncedStartDate)
      const endDateStr = formatDateForApi(debouncedEndDate)

      // Call export API
      const blob = await trackingLogApi.exportTrackingLogs(
        undefined, // employeeId - not used for now
        employeeName,
        startDateStr,
        endDateStr
      )

      // Create download link
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      
      // Generate filename with current date
      const now = new Date()
      const dateStr = now.toISOString().split('T')[0] // YYYY-MM-DD
      link.download = `tracking-logs-${dateStr}.csv`
      
      // Trigger download
      document.body.appendChild(link)
      link.click()
      
      // Cleanup
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Export error:', err)
      setErrorMessage(getErrorMessage(err))
      setShowErrorSnackbar(true)
    }
  }

  // Handle date picker
  const handleOpenDatePicker = () => {
    setDatePicker({
      isOpen: true,
      onClose: () => setDatePicker(null),
      onApply: (start: Date | null, end: Date | null) => {
        setStartDate(start)
        setEndDate(end)
        setDatePicker(null)
      },
      startDate,
      endDate,
      anchorRef: dateButtonRef
    })
  }

  // Handle remove date filter
  const handleRemoveDateFilter = () => {
    setStartDate(null)
    setEndDate(null)
  }


  // Table headers
  const header = ['Employee Name', 'Activity', 'Page View', 'Timestamp']

  // Render table row
  const renderRow = (item: TrackingLogDto) => {
    return (
      <>
        {/* Employee Name */}
        <td className="px-6 py-4 whitespace-nowrap">
          <span
            className="text-sm font-medium"
            style={{ color: theme.color.gray500 }}
          >
            {item.employeeName}
          </span>
        </td>
        {/* Activity */}
        <td className="px-6 py-4 whitespace-nowrap">
          <span
            className="text-sm"
            style={{ color: theme.color.gray500 }}
          >
            {item.activity}
          </span>
        </td>
        {/* Page View */}
        <td className="px-6 py-4 whitespace-nowrap">
          <span
            className="text-sm"
            style={{ color: theme.color.gray500 }}
          >
            {item.pageView}
          </span>
        </td>
        {/* Timestamp */}
        <td className="px-6 py-4 whitespace-nowrap">
          <span
            className="text-sm"
            style={{ color: theme.color.gray500 }}
          >
            {formatDate(item.timestamp, 'withTime')}
          </span>
        </td>
      </>
    )
  }

  // Calculate table height based on filter visibility
  const hasFilters = !!(startDate || endDate)
  const tableHeight = hasFilters ? 'calc(100vh - 350px)' : 'calc(100vh - 295px)'

  return (
    <Layout currentPath="/tracking-log">
      {/* Error SnackBar */}
      {showErrorSnackbar && (
        <SnackBar
          message={errorMessage}
          type="error"
          position="top-right"
          show={showErrorSnackbar}
          onClose={() => setShowErrorSnackbar(false)}
        />
      )}
      <div className="flex flex-col">
        {/* Page Header */}
        <div className="flex justify-between items-center mb-6">
          <h1
            className="text-2xl font-medium"
            style={{ color: theme.color.gray500 }}
          >
            Tracking Log
          </h1>
          <button
            onClick={handleExport}
            className="flex items-center px-4 py-2 rounded-lg text-white font-medium transition-colors hover:shadow-lg"
            style={{ backgroundColor: theme.brandColor.red }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = theme.brandColor.red
              e.currentTarget.style.opacity = '0.9'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = theme.brandColor.red
              e.currentTarget.style.opacity = '1'
            }}
          >
            {React.createElement(IoDownloadOutline as any, {
              className: "w-5 h-5 mr-2"
            })}
            Export Excel
          </button>
        </div>

        {/* Search and Filter Bar */}
        <div className="mb-6 flex items-center gap-4">
          {/* Search Bar */}
          <div className="relative flex-1">
            <Input
              type="text"
              placeholder="Search employee name"
              value={searchQuery}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setSearchQuery(e.target.value)
                // Page reset will be handled by debounce effect
              }}
              className="!pl-12"
            />
            <div
              className="absolute left-4"
              style={{
                top: '50%',
                transform: 'translateY(-50%)',
                color: theme.color.gray200,
                pointerEvents: 'none'
              }}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </div>

          {/* Date Filter Button */}
          <button
            ref={dateButtonRef}
            onClick={handleOpenDatePicker}
            className="flex items-center px-4 py-3 rounded-lg border transition-colors hover:shadow-sm"
            style={{
              backgroundColor: theme.color.light100,
              borderColor: theme.color.light500,
              color: theme.color.gray500
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = theme.color.light400
              e.currentTarget.style.backgroundColor = theme.color.light200
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = theme.color.light500
              e.currentTarget.style.backgroundColor = theme.color.light100
            }}
          >
            {React.createElement(IoCalendarOutline as any, {
              className: "w-5 h-5 mr-2"
            })}
            Date
          </button>
        </div>

        {/* Active Filters */}
        {(startDate || endDate) && (
          <div className="mb-4 flex items-center">
            <span
              className="text-sm mr-3"
              style={{ color: theme.color.gray400 }}
            >
              Active filters:
            </span>
            <div className="flex items-center px-3 py-1.5 rounded-full"
              style={{ backgroundColor: theme.brandColor.red }}
            >
              <span
                className="text-sm font-medium mr-2"
                style={{ color: theme.color.light100 }}
              >
                {formatDateForTag(startDate, endDate)}
              </span>
              <button
                onClick={handleRemoveDateFilter}
                className="flex items-center justify-center w-4 h-4 rounded-full hover:bg-red-700 transition-colors"
                style={{ color: theme.color.light100 }}
              >
                {React.createElement(IoClose as any, {
                  className: "w-3 h-3"
                })}
              </button>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div
            className="mb-4 p-4 rounded-lg text-sm"
            style={{
              backgroundColor: `${theme.color.error}20`,
              borderColor: `${theme.color.error}50`,
              color: theme.color.error,
              border: '1px solid'
            }}
          >
            {error}
          </div>
        )}

        {/* Table Container */}
        <div className="flex flex-col flex-1" style={{ height: tableHeight, minHeight: 0, maxHeight: tableHeight }}>
          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2" style={{ borderColor: theme.brandColor.red }}></div>
            </div>
          )}

          {/* Table */}
          {!isLoading && (
            <Table
              data={trackingLogs}
              className={`${totalPages > 1 ? 'pb-0' : ''}`}
              header={header}
              renderRow={renderRow}
            />
          )}
        </div>

        {/* Pagination - Fixed at bottom */}
        {!isLoading && totalPages > 0 && (
          <div className="fixed bottom-0 left-64 right-0 mx-6 py-4 z-10">
            <div className="flex justify-center">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                itemsPerPage={itemsPerPage}
                totalItems={totalItems}
              />
            </div>
          </div>
        )}

        {/* DatePicker Period Popover */}
        {datePicker && (
          <DatePickerPeriod
            isOpen={datePicker.isOpen}
            onClose={datePicker.onClose}
            onApply={datePicker.onApply}
            startDate={datePicker.startDate}
            endDate={datePicker.endDate}
            anchorRef={datePicker.anchorRef}
          />
        )}
      </div>
    </Layout>
  )
}

export default TrackingLogPage

